import { parseChecklistClause } from "~/utilities/checklistQueryParse";

/** Minimal filter shape for resolving keys / names from screener JSON. */
export interface ScreenerFilterLookup {
    key: string;
    name: string;
}

const FILTER_OPS = ["<=", ">=", "!=", "==", "<", ">", "="] as const;

/**
 * Single filter clause (`field op value`). Uses the same leftmost-operator rules as checklist/screener API parsing.
 */
export function parseScreenerFilterLine(line: string): { field: string; op: string; value: string } | null {
    const p = parseChecklistClause(line);
    if (!p) return null;
    return { field: p.field, op: p.operator, value: p.value };
}

export function findFilterByKeyOrName(
    filterList: ScreenerFilterLookup[],
    token: string
): ScreenerFilterLookup | undefined {
    const t = token.trim();
    const byKey = filterList.find((f) => f.key.toLowerCase() === t.toLowerCase());
    if (byKey) return byKey;
    return filterList.find((f) => f.name.toLowerCase() === t.toLowerCase());
}

function isEnumValueEntry(v: unknown): v is { name: string; value: string } {
    return (
        typeof v === 'object' &&
        v !== null &&
        'value' in v &&
        typeof (v as { value: unknown }).value === 'string'
    );
}

/** Screener row shape needed to resolve education-panel example strings (display name or key + value). */
export type ExampleClauseFilter = ScreenerFilterLookup & {
    type?: string;
    values?: unknown;
};

/**
 * Parse one example clause (e.g. `Market Cap >= 10M` or `market_cap >= 10M`) into UI + API triples.
 */
export function flatTriplesFromExampleClause(
    query: string,
    filterList: ExampleClauseFilter[]
): { displayTriple: string[]; apiTriple: string[]; filterItem: ScreenerFilterLookup } | null {
    const parsed = parseScreenerFilterLine(query.trim());
    if (!parsed) return null;
    const filterItem = findFilterByKeyOrName(filterList, parsed.field);
    if (!filterItem) return null;

    const full = filterList.find((f) => f.key === filterItem.key);
    const op = parsed.op;
    const raw = parsed.value;

    const t = full?.type?.toLowerCase();
    if (t === 'int' || t === 'float') {
        return {
            filterItem,
            displayTriple: [filterItem.name, op, raw],
            apiTriple: [filterItem.key, op, raw],
        };
    }

    const values = full?.values;
    if (Array.isArray(values) && values.length > 0 && isEnumValueEntry(values[0])) {
        const rawLower = raw.toLowerCase();
        const list = values as { name: string; value: string }[];
        const match = list.find(
            (item) =>
                String(item.value).toLowerCase() === rawLower ||
                String(item.name).toLowerCase() === rawLower
        );
        if (!match) return null;
        const displayVal = match.name ?? match.value;
        return {
            filterItem,
            displayTriple: [filterItem.name, op, displayVal],
            apiTriple: [filterItem.key, op, match.value],
        };
    }

    return {
        filterItem,
        displayTriple: [filterItem.name, op, raw],
        apiTriple: [filterItem.key, op, raw],
    };
}

export interface BuiltIdeaQuery {
    displayFlat: string[];
    apiFlat: string[];
    filterItems: ScreenerFilterLookup[];
}

/**
 * Turns parallel display / API idea lines into flat triples for UI and POST body.
 */
export function buildFlatQueryFromScreenIdeaLines(
    filterList: ScreenerFilterLookup[],
    displayLines: string[],
    apiLines: string[]
): BuiltIdeaQuery {
    const displayFlat: string[] = [];
    const apiFlat: string[] = [];
    const filterItems: ScreenerFilterLookup[] = [];

    const n = Math.min(displayLines.length, apiLines.length);
    for (let i = 0; i < n; i++) {
        const d = parseScreenerFilterLine(displayLines[i]);
        const a = parseScreenerFilterLine(apiLines[i]);
        if (!d && !a) continue;

        const fieldToken = d?.field ?? a!.field;
        const filterItem = findFilterByKeyOrName(filterList, fieldToken);
        if (!filterItem) continue;

        const op = d?.op ?? a!.op;
        const displayValue = d?.value ?? a!.value;
        const apiValue = a?.value ?? d!.value;

        displayFlat.push(filterItem.name, op, displayValue);
        apiFlat.push(filterItem.key, op, apiValue);
        filterItems.push(filterItem);
    }

    return { displayFlat, apiFlat, filterItems };
}

/** Between filter clauses in `?query=` — avoids splitting on hyphens inside metric names or values. */
export const SCREENER_QUERY_CLAUSE_SEPARATOR = '~~~';

const SCREENER_SORT_COLUMN_PATTERN = /^[a-z][a-z0-9_]*$/i;

export function parseScreenerUrlSort(urlParams: URLSearchParams): {
  sortBy: string;
  sortDirection: 'asc' | 'desc';
} {
  const rawSortBy = urlParams.get('sort_by');
  const sortBy =
    rawSortBy && SCREENER_SORT_COLUMN_PATTERN.test(rawSortBy) ? rawSortBy : 'market_cap';
  const sortDirection = urlParams.get('sort_direction') === 'asc' ? 'asc' : 'desc';

  return { sortBy, sortDirection };
}

/**
 * Scores a single filter item against the user's query.
 *
 * Tiers (higher = better match):
 *   100 — name or key is exactly the query
 *    80 — name or key starts with the full query string  (prefix boost)
 *    60 — full query appears anywhere as a substring
 *    40 — name/key starts with the first typed word AND all words match somewhere
 *    20 — every typed word matches somewhere (original behaviour, now ranked)
 *     0 — no match → item excluded
 */
function scoreFilterItem(
    name: string,
    key: string,
    query: string,
    words: string[]
): number {
    if (name === query || key === query) return 100;
    if (name.startsWith(query) || key.startsWith(query)) return 80;
    if (name.includes(query) || key.includes(query)) return 60;

    // All individual words must match somewhere before we award partial scores.
    const allWordsMatch = words.every((w) => name.includes(w) || key.includes(w));
    if (!allWordsMatch) return 0;

    // Reward when the first typed word is a prefix (e.g. "rev" → "Revenue Growth" > "Total Revenue").
    if (name.startsWith(words[0]) || key.startsWith(words[0])) return 40;

    return 20;
}

/**
 * Returns `filterList` items that match `query`, sorted by relevance score (desc).
 * Items with equal scores preserve their original JSON order (stable sort).
 *
 * Drop-in replacement for the `.filter(…includes…)` pattern — accepts the same
 * FilterItem array and returns the same type, so no call-site changes are needed
 * beyond swapping the useEffect body.
 */
export function rankFilterSuggestions<T extends { name: string; key: string }>(
    filterList: T[],
    query: string
): T[] {
    const q = query.trim().toLowerCase();

    // Empty query → show everything (fast path, no scoring needed).
    if (!q) return filterList;

    const words = q.split(/\s+/);

    const scored: { item: T; score: number }[] = [];
    for (const item of filterList) {
        const score = scoreFilterItem(
            item.name.toLowerCase(),
            item.key.toLowerCase(),
            q,
            words
        );
        if (score > 0) scored.push({ item, score });
    }

    // Primary sort: score descending.
    // Tie-breaker: shorter name ascending — "Revenue" ranks above "Revenue Per Share (TTM)"
    // when both start with the query, because less extra text = closer match.
    scored.sort((a, b) =>
        b.score - a.score || a.item.name.length - b.item.name.length
    );

    return scored.map((s) => s.item);
}

function isScreenerOp(token: string): boolean {
    return (FILTER_OPS as readonly string[]).includes(token as (typeof FILTER_OPS)[number]);
}

/**
 * `apiDataArr.join('-')` / flat API triples in the URL (e.g. `market_cap->=-20B-key->=-5%`).
 * Not the same as legacy space-separated clauses joined by `-`.
 */
function tryHyphenDelimitedFlatTriples(
    query: string,
    filterList: ScreenerFilterLookup[]
): { displayFlat: string[]; apiFlat: string[]; filterItems: ScreenerFilterLookup[]; clauseCount: number } | null {
    const q = query.trim();
    if (!q || q.includes(SCREENER_QUERY_CLAUSE_SEPARATOR)) return null;

    const tokens = q.split('-').map((s) => s.trim()).filter(Boolean);
    if (tokens.length < 3 || tokens.length % 3 !== 0) return null;

    const displayFlat: string[] = [];
    const apiFlat: string[] = [];
    const filterItems: ScreenerFilterLookup[] = [];

    for (let i = 0; i < tokens.length; i += 3) {
        const fieldToken = tokens[i];
        const op = tokens[i + 1];
        const value = tokens[i + 2];
        if (!isScreenerOp(op)) return null;
        const filterItem = findFilterByKeyOrName(filterList, fieldToken);
        if (!filterItem) return null;
        displayFlat.push(filterItem.name, op, value);
        apiFlat.push(filterItem.key, op, value);
        filterItems.push(filterItem);
    }

    return { displayFlat, apiFlat, filterItems, clauseCount: filterItems.length };
}

export function splitEncodedQueryToClauses(query: string): string[] {
    const q = query.trim();
    if (!q) return [];
    if (q.includes(SCREENER_QUERY_CLAUSE_SEPARATOR)) {
        return q.split(SCREENER_QUERY_CLAUSE_SEPARATOR).map((s) => s.trim()).filter(Boolean);
    }

    // Legacy links split clauses on `-`. Names like "Revenue Growth (3Y CAGR)" must stay one clause.
    const whole = parseScreenerFilterLine(q);
    if (whole) {
        const nested = parseScreenerFilterLine(whole.value);
        if (!nested) {
            return [q];
        }
    }

    return q.split('-').map((s) => s.trim()).filter(Boolean);
}

/**
 * Parse `?query=` into flat display/API triples (keys resolved via filterList).
 * Supports `~~~` between clauses, space-separated clauses, hyphenated display names,
 * legacy `-` between clauses, and hyphen-joined flat API triples (`key->=10M-key2->=5%`).
 */
export function flatTriplesFromUrlQuery(
    query: string,
    filterList: ScreenerFilterLookup[]
): { displayFlat: string[]; apiFlat: string[]; filterItems: ScreenerFilterLookup[]; clauseCount: number } | null {
    const clauses = splitEncodedQueryToClauses(query);
    const displayFlat: string[] = [];
    const apiFlat: string[] = [];
    const filterItems: ScreenerFilterLookup[] = [];

    for (const clause of clauses) {
        const parsed = parseScreenerFilterLine(clause);
        if (!parsed) continue;
        const filterItem = findFilterByKeyOrName(filterList, parsed.field);
        if (!filterItem) continue;
        displayFlat.push(filterItem.name, parsed.op, parsed.value);
        apiFlat.push(filterItem.key, parsed.op, parsed.value);
        filterItems.push(filterItem);
    }

    if (apiFlat.length === 0) {
        const fromFlat = tryHyphenDelimitedFlatTriples(query.trim(), filterList);
        if (fromFlat) return fromFlat;
        return null;
    }
    return { displayFlat, apiFlat, filterItems, clauseCount: filterItems.length };
}
